import { validTiming, timingFields, validClock } from '../models/transient/interpolation';
import { validPoiCollection, validInitialPoiCollection } from '../models/transient/point-of-interest-validation';
import { EGameStateEnum, IStateWrapper, IPlayer, EGameModeEnum, EStateKeys, IMiscState, IGameState, IHeroState, IUpgradeState, IPlayerStateWrapper, EResource, User, ISettingsState, IPlayerStats, IMainAccount, MatchOutcomes } from '../models';
import { diff } from 'deep-diff';

const validTimedProgress = (value: any): boolean => !!value && validTiming(value) &&
    (value.progress === null || Number.isFinite(value.progress) && value.progress >= 0 && value.progress <= 1) &&
    (value.remainingSeconds === null && value.totalSeconds === null ||
        Number.isFinite(value.remainingSeconds) && value.remainingSeconds >= 0 &&
        Number.isFinite(value.totalSeconds) && value.totalSeconds > 0 && value.remainingSeconds <= value.totalSeconds);
const timedProgress = (value: any) => ({ progress: value.progress, remainingSeconds: value.remainingSeconds, totalSeconds: value.totalSeconds, ...timingFields(value) });
const damageRecord = (value: any): boolean => !!value && typeof value === 'object' &&
    !Array.isArray(value) && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const finiteDamage = (value: any): boolean => Number.isFinite(value) && value >= 0;
const validDamageSummary = (value: any, engineTotal: number): boolean => {
  if (!damageRecord(value) || !finiteDamage(value.total) || typeof value.complete !== 'boolean' ||
    !Array.isArray(value.breakdown) || !value.breakdown.length || value.breakdown.length > 256) return false;
  const allTypes: Set<string> = new Set<string>();
  let sum = 0;
  for (const contribution of value.breakdown) {
    if (!damageRecord(contribution) || !finiteDamage(contribution.damageDealt) || !Array.isArray(contribution.units) ||
      !contribution.units.length || contribution.units.length > 128) return false;
    for (const unit of contribution.units) {
      if (!damageRecord(unit) || typeof unit.typeId !== 'string' || !/^[A-Za-z0-9]{4}$/.test(unit.typeId) || typeof unit.isIllusion !== 'boolean') return false;
      const key = unit.typeId + ':' + unit.isIllusion;
      if (allTypes.has(key)) return false;
      allTypes.add(key);
    }
    sum += contribution.damageDealt;
  }
  const tolerance = Math.max(0.00001, value.total * 0.000001);
  return Number.isFinite(sum) && Math.abs(sum - value.total) <= tolerance && value.total + tolerance >= engineTotal;
};
const damageSummary = (value: any) => ({ total: value.total, complete: value.complete,
  breakdown: value.breakdown.map((contribution: any) => ({ damageDealt: contribution.damageDealt,
    units: contribution.units.map((unit: any) => ({ typeId: unit.typeId, isIllusion: unit.isIllusion })) })) });

const analyticsRecord = value => !!value && typeof value === 'object' && !Array.isArray(value) &&
  [Object.prototype, null].includes(Object.getPrototypeOf(value));
const analyticsCount = value => Number.isInteger(value) && value >= 0 && value <= 0xffffffff;
const analyticsTime = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const analyticsTypes = (value, check) => analyticsRecord(value) && Object.keys(value).length <= 999 &&
  Object.entries(value).every(([key, entry]) => /^[A-Za-z0-9]{4}$/.test(key) && check(entry));
const analyticsDamage = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const validAbilityStatistics = value => analyticsRecord(value) &&
  (value.level === undefined || analyticsCount(value.level)) &&
  (value.isHeroAbility === undefined || typeof value.isHeroAbility === 'boolean') &&
  (value.cooldown === undefined || validTimedProgress(value.cooldown)) && ['damageDealt', 'healingDone'].every(key => analyticsDamage(value[key]));
const validHeroStatistics = value => analyticsRecord(value) && typeof value.typeId === 'string' && /^[A-Z][A-Za-z0-9]{3}$/.test(value.typeId) &&
  (value.level === undefined || analyticsCount(value.level) && value.level > 0) &&
  (value.nextLevelExperience === undefined || analyticsCount(value.nextLevelExperience)) &&
  ['deaths', 'totalKills', 'heroKills', 'selfKills', 'buildingKills', 'timeAliveMs'].every(key => analyticsCount(value[key])) &&
  (value.abilities === undefined || analyticsTypes(value.abilities, validAbilityStatistics));
const validControlGroups = value => analyticsRecord(value) && Object.entries<any>(value).every(([key, group]) =>
  /^[0-9]$/.test(key) && analyticsRecord(group) && typeof group.frontunit === 'string' &&
  Number.isInteger(group.size) && group.size >= 0 && group.size <= 4096 &&
  (group.members === undefined || Array.isArray(group.members) && group.members.length === group.size &&
    new Set(group.members.map(member => member?.unitId)).size === group.members.length &&
    group.members.every(member => analyticsRecord(member) && /^[0-9a-f]{16}$/.test(member.unitId) &&
      member.unitId !== '0000000000000000' && /^[A-Za-z0-9]{4}$/.test(member.typeId))));

const validUnitInstances = value => analyticsRecord(value) && Object.keys(value).length <= 4096 &&
  Object.entries<any>(value).every(([id, unit]) => /^[0-9a-f]{16}$/.test(id) && id !== '0000000000000000' && analyticsRecord(unit) &&
    /^[A-Za-z0-9]{4}$/.test(unit.typeId) && typeof unit.alive === 'boolean' &&
    ['damageDealt', 'damageReceived', 'healingDone'].every(key => analyticsDamage(unit[key])));
const validUnitTypeStatistics = value => analyticsRecord(value) && ['currentAmount', 'totalAmount'].every(key => analyticsCount(value[key])) &&
  value.currentAmount <= value.totalAmount && typeof value.isPeon === 'boolean' && typeof value.isFunctionalPeon === 'boolean' &&
  ['damageDealt', 'damageReceived', 'healingDone'].every(key => analyticsDamage(value[key]));
const validPlayerStatistics = value => analyticsRecord(value) && analyticsTime(value.gameTime) &&
  (value.handicapPercent === undefined || analyticsDamage(value.handicapPercent) && value.handicapPercent <= 1000) &&
  (value.racePreference === undefined || analyticsCount(value.racePreference) && value.racePreference <= 127) &&
  (value.playerRace === undefined || analyticsCount(value.playerRace) && value.playerRace <= 11) &&
  (value.realTimeApm === undefined || analyticsCount(value.realTimeApm)) &&
  ['slotState', 'aiDifficulty'].every(key => value[key] === undefined || [0,1,2].includes(value[key])) &&
  (value.timeInUpkeepMs === undefined || Array.isArray(value.timeInUpkeepMs) && value.timeInUpkeepMs.length === 10 && value.timeInUpkeepMs.every(analyticsCount)) &&
  (value.units === undefined || analyticsTypes(value.units, validUnitTypeStatistics)) &&
  (value.unitInstances === undefined || validUnitInstances(value.unitInstances)) &&
  (value.heroes === undefined || analyticsRecord(value.heroes) && Object.keys(value.heroes).length <= 256 &&
    Object.entries(value.heroes).every(([id, hero]) => /^[0-9a-f]{16}$/.test(id) && validHeroStatistics(hero))) &&
  ['goldMined', 'goldUpkeepLost', 'goldCredited', 'goldDiversionTax', 'lumberCredited', 'lumberUpkeepLost', 'lumberDiversionTax'].every(key => value[key] === undefined || analyticsCount(value[key])) &&
  (value.items === undefined || analyticsTypes(value.items, item => analyticsRecord(item) &&
    ['collected', 'purchased', 'sold', 'used', 'destroyed'].every(key => analyticsCount(item[key])) &&
    ['damageDealt', 'healingDone'].every(key => item[key] === undefined || analyticsCount(item[key]))));
const validPlayerLosses = value => analyticsRecord(value) && analyticsTime(value.gameTime) && typeof value.complete === 'boolean' &&
  (value.units === undefined || analyticsTypes(value.units, analyticsCount)) &&
  (value.buildings === undefined || analyticsTypes(value.buildings, analyticsCount));
const validMatchOutcomes = (value: any): value is MatchOutcomes => analyticsRecord(value) && Object.keys(value).length <= 24 &&
  Object.entries(value).every(([key, entry]) => /^(?:[0-9]|1[0-9]|2[0-3])$/.test(key) && typeof entry === 'string' && ['won', 'lost', 'draw'].includes(entry));

export abstract class AbstractStateManagerService {

    public abstract getSettings(observerSettings?: boolean): Promise<ISettingsState>;
    public abstract getParseUser(): User;
    public abstract sendStateDiff(key: EStateKeys, differences: Array<any>);
    public abstract sendStateSnapshot(key: EStateKeys);
    public abstract sendAllStateSnapshots();
    public abstract getPlayerStats(player: IPlayer, realm: string): Promise<IPlayerStats>;
    public abstract getPlayerMainAccount(player: IPlayer, realm: string): Promise<IMainAccount>;
    public abstract onGameCreationStarted(rawGameData: any);
    public abstract onGameCreationFinished(state: IStateWrapper);

    private state: IStateWrapper;
    private playerStates: Map<number, IPlayerStateWrapper>;

    public isGameRunning(): boolean {
        return (this.state.game.state === EGameStateEnum.GAME_START || this.state.game.state === EGameStateEnum.GAME_RUNNING);
    }

    public isObserverOrReplay(): boolean {
        return (this.state.game.isObserver || this.state.game.isReplay);
    }

    public initByState(state: IStateWrapper, playerStates?: { [playerId: number]: IPlayerStateWrapper }) {
        this.resetState();
        this.state = state;

        if (this.state.game && (this.state.game.state === EGameStateEnum.GAME_START || this.state.game.state === EGameStateEnum.GAME_RUNNING)) {
            for (const playerId of Object.keys(this.state.game.players)) {
                this.initPlayerState(Number(playerId));
            }

            if (playerStates) {
                for (const playerId of Object.keys(playerStates)) {
                    this.playerStates.set(Number(playerId), playerStates[playerId]);
                }
            }

            this.onAfterBroadcasterChange();
        }
        this.sendAllStateSnapshots();

    }

    public getState(): IStateWrapper {
        return this.state;
    }

    public getPlayerStates() {
        return this.playerStates;
    }

    public resetState() {
        // make sure, to cancel the delayed callback or the in research being upgrades are shown after the game was
        this.initState();
    }

    public updateGameResult(raw: any): void {
        if (!raw || String(raw.id) !== String(this.state.game.id) || this.state.game.state === EGameStateEnum.NO_GAME) return;
        const won = typeof raw.result?.won === 'boolean' ? raw.result.won : raw.isWon;
        if (typeof raw.isReplay === 'boolean') this.state.game.isReplay = raw.isReplay;
        if (typeof raw.isObserver === 'boolean') this.state.game.isObserver = raw.isObserver;
        // The recorder's outcome refers to its actual local player, never the
        // observer's currently selected player or a replay's viewing slot.
        if (typeof won === 'boolean' && raw.isReplay === false && raw.isObserver === false &&
            ['number', 'string'].includes(typeof raw.broadcasterId)) {
            this.state.game.result = { playerId: String(raw.broadcasterId), outcome: won ? 'won' : 'lost' };
        }
        if (validMatchOutcomes(raw.outcomes) && Object.keys(raw.outcomes).every(id => this.state.game.players[id]))
            this.state.game.outcomes = Object.fromEntries(Object.entries<MatchOutcomes[string]>(raw.outcomes).filter(([id]) =>
                this.isObserverOrReplay() || id === String(this.state.game.realBroadcasterId)));
        if (Number.isFinite(raw.gameTime) && raw.gameTime >= 0) this.state.game.gameTime = Math.floor(raw.gameTime);
        this.state.game.state = EGameStateEnum.GAME_FINISHED;
        this.state.game.end ??= new Date();
        this.state.misc.localServerUrls = [];
        this.sendAllStateSnapshots();
    }

    public async updateGameState(state: EGameStateEnum, rawGameData?) {
        // Keep the terminal snapshot available for SDK hydration/reconnects until
        // the next game. A recorder returning to its idle loop is not a new match.
        if (state === EGameStateEnum.NO_GAME && this.state.game.state === EGameStateEnum.GAME_FINISHED) return;
        if (this.state.game.state !== state && state !== EGameStateEnum.GAME_RUNNING) {
            this.state.game.state = state;
            if (state === EGameStateEnum.GAME_FINISHED) {
                this.state.game.end ??= new Date();
                this.state.misc.localServerUrls = [];
                this.sendAllStateSnapshots();
            } else if (state === EGameStateEnum.NO_GAME) {
                this.resetState();
                this.sendAllStateSnapshots();
            } else if (state === EGameStateEnum.GAME_START) {
                this.resetState();
                try {
                    if (typeof rawGameData.game.name === 'string') rawGameData.game.name = decodeURIComponent(rawGameData.game.name);
                    rawGameData.game.creator = decodeURIComponent(rawGameData.game.creator);
                    rawGameData.game.map = decodeURIComponent(rawGameData.game.map);
                    for (const index of Object.keys(rawGameData.players)) {
                        rawGameData.players[index].name = decodeURIComponent(rawGameData.players[index].name);
                    }
                } catch(e) {
                    console.warn('UNABLE TO DECODE SOME COMPONENT!')
                    console.warn(JSON.stringify(rawGameData));
                }


                console.log('NEW GAME RAW DATA:');
                console.log(JSON.stringify(rawGameData));

                this.state.misc.localServerUrls = rawGameData.localServerUrls;
                this.state.game.gameName = typeof rawGameData.game.name === 'string' && rawGameData.game.name.length <= 256 ? rawGameData.game.name : undefined;
                this.state.game.id = rawGameData.id;
                this.state.game.gameDataId = typeof rawGameData.gameDataId === 'string' && /^\d+(?:\.\d+){3}-[a-f0-9]{16}$/.test(rawGameData.gameDataId) ? rawGameData.gameDataId : undefined;
                this.state.game.gameVersion = typeof rawGameData.gameVersion === 'string' && /^\d+(?:\.\d+){3}$/.test(rawGameData.gameVersion) ? rawGameData.gameVersion : undefined;
                this.state.game.statsContext = typeof rawGameData.statsContext === "string" && rawGameData.statsContext.length <= 160 ? rawGameData.statsContext : undefined;
                this.state.game.state = EGameStateEnum.GAME_START;
                this.state.game.isReplay = rawGameData.isReplay;
                this.state.game.isReforged = rawGameData.isReforged;
                this.state.game.isObserver = rawGameData.isObserver;
                this.state.game.broadcasterId = rawGameData.broadcasterId;
                this.state.game.realBroadcasterId = rawGameData.broadcasterId;
                this.state.game.realm = rawGameData.realm;
                this.state.game.start = new Date();
                this.state.game.players = {};
                this.state.game.map = rawGameData.game.map.split('/').reverse()[0].split('\\').reverse()[0].replace(/\.w3[xm]{1}/g, '').replace(/([A-Z]{1}[a-z])/g, ' $1').replace(/\([0-9]+\)/g, '').replace(/[\_\-]/g, ' ').trim();
                this.state.game.paused = false;

                // New recorders attach the presentation values read alongside
                // W3Game. Apply them before the first state snapshots so apps
                // never render a match at the default scale and resize later.
                // Unavailable measurements use an explicit default, never the
                // preceding match's context.
                this.state.misc.hudScale = 1;
                delete this.state.misc.heroBarLastOccupiedSlot;
                delete this.state.misc.chatbarOpen;
                delete this.state.misc.menuOpen;
                delete this.state.misc.advancedObserverMode;
                delete this.state.misc.teamColors;
                const initialRuntime = rawGameData.initialRuntime;
                if (initialRuntime && typeof initialRuntime === 'object') {
                    const lastHeroSlot = initialRuntime.heroBarLastOccupiedSlot;
                    if (typeof lastHeroSlot === 'number' && Number.isInteger(lastHeroSlot) && lastHeroSlot >= 0 && lastHeroSlot <= 32) {
                        this.state.misc.heroBarLastOccupiedSlot = lastHeroSlot;
                    }
                    const hudScale = this.validateHudScale(initialRuntime.hudScale);
                    if (hudScale !== undefined) this.state.misc.hudScale = hudScale;
                    if (typeof initialRuntime.advancedObserverMode === 'boolean') this.state.misc.advancedObserverMode = initialRuntime.advancedObserverMode;
                    if (typeof initialRuntime.menuOpen === 'boolean') this.state.misc.menuOpen = initialRuntime.menuOpen;
                    if (typeof initialRuntime.chatbarOpen === 'boolean') {
                        this.state.misc.chatbarOpen = initialRuntime.chatbarOpen;
                    }
                    if (typeof initialRuntime.teamColors === 'boolean') {
                        this.state.misc.teamColors = initialRuntime.teamColors;
                    }
                }

                // Initialize players
                for (const player of Object.values(rawGameData.players as { [id: number]: IPlayer })) {
                    if (player.team !== undefined && player.team !== 24) {
                        delete (player as any).name_buffer;
                        if ((!rawGameData.isObserver && !rawGameData.isReplay && player.id !== rawGameData.broadcasterId) ||
                            !validControlGroups(player.controlgroups)) delete player.controlgroups;
                        player.stats = { status: player.isAI ? 'unavailable' : 'loading', records: [] };
                        this.state.game.players[player.id] = player;
                        this.initPlayerState(player.id);
                    }
                }

                // detect gamemode
                const teamArray = Object.values(this.state.game.players).map(p => p.team);
                const teams = new Set(teamArray);
                if (teams.size === 2) {
                    let index = 0;
                    const counts = new Array();
                    for (const team of teams) {
                        counts[index++] = teamArray.filter(t => t === team).length;
                    }
                    const isSymetric = counts[0] === counts[1];

                    if (!isSymetric || teamArray.length > 8) {
                        this.state.game.gameMode = EGameModeEnum.GM_CUSTOM;
                    } else {
                        switch (teamArray.length) {
                            case 2: {
                                this.state.game.gameMode = EGameModeEnum.GM_1V1;
                            } break;
                            case 4: {
                                this.state.game.gameMode = EGameModeEnum.GM_2V2;
                            } break;
                            case 6: {
                                this.state.game.gameMode = EGameModeEnum.GM_3V3;
                            } break;
                            case 8: {
                                this.state.game.gameMode = EGameModeEnum.GM_4V4;
                            } break;
                        }
                    }
                } else if (teamArray.length === teams.size && teams.size >= 3 && teams.size <= 4) {
                    this.state.game.gameMode = (teams.size === 3) ? EGameModeEnum.GM_3FFA : EGameModeEnum.GM_4FFA;
                } else {
                    this.state.game.gameMode = EGameModeEnum.GM_CUSTOM;
                }

                this.initByState(this.state);
                this.state.settings = await this.getSettings(this.isObserverOrReplay());

                if (!this.isObserverOrReplay()) {
                    this.getBroadcastingPlayer().mainAccount = { name: this.state.settings.username ? this.state.settings.username : this.getParseUser().displayName, country: this.state.settings.nationality };
                }

                console.log('Finished game initialization:');
                console.log(this.state);
                this.sendAllStateSnapshots();
                this.onGameCreationStarted(rawGameData);

                // Do all the async stuff
                const asyncPromissesToWaitFor = new Array<Promise<any>>();
                for (const player of Object.values(this.state.game.players)) {
                    const realm = rawGameData.realm;
                    const rawGameDataPlayer = rawGameData.players[player.id];
                    // fetch stats
                    const playerStatsPromise = this.getPlayerStats(rawGameDataPlayer, realm);
                    playerStatsPromise.then((results) => {
                        if(this.isGameRunning() && this.state.game.id === rawGameData.id && this.state.game.statsContext === rawGameData.statsContext) {
                            console.log('Determined stats for ' + player.name);
                            console.log(results);
                            const current = this.state.game.players[player.id].stats;
                            // Server lookups supply W3C; desktop Battle.net envelopes arrive independently.
                            const records = [...(current?.records || []).filter(record => record.provider !== 'w3champions'),
                                ...(results?.records || [])];
                            this.state.game.players[player.id].stats = { ...current,
                                status: records.length ? 'ready' : (realm === 'Reforged' && rawGameData.statsContext && !rawGameData.isReplay
                                    && current?.status === 'loading' ? 'loading' : 'unavailable'), records };
                        }
                    });
                    asyncPromissesToWaitFor.push(playerStatsPromise);

                    if (player.mainAccount === undefined) {
                        const mainAccountPromise = this.getPlayerMainAccount(rawGameDataPlayer, realm);
                        mainAccountPromise.then((mainAccount) => {
                            if (this.state.game.id === rawGameData.id && this.state.game.statsContext === rawGameData.statsContext) this.state.game.players[player.id].mainAccount = mainAccount;
                        });
                        asyncPromissesToWaitFor.push(mainAccountPromise);
                    }
                }

                const gameId = this.state.game.id;
                Promise.all(asyncPromissesToWaitFor).then(() => {
                    if (this.state && this.state.game && this.state.game.id === gameId && this.isGameRunning()) {
                        const gameState = JSON.parse(JSON.stringify(this.state.game)) as IGameState;
                        for (const player of Object.values(gameState.players)) {
                            player.stats = undefined;
                            player.mainAccount = undefined;
                            //
                        }
                        this.state.game.state = EGameStateEnum.GAME_RUNNING;
                        const gameStateDifferences = diff(gameState, this.state.game);
                        if (gameStateDifferences) {
                            this.sendStateDiff(EStateKeys.GAME_STATE, gameStateDifferences);
                        }
                        this.onGameCreationFinished(this.state);
                    }
                });
            } else {
                this.sendStateSnapshot(EStateKeys.GAME_STATE);
            }
        }
    }

    public updateStateByUpdate(update) {
        let playerId = -1;
        try {

            if (!this.isGameRunning()) {
                console.warn('received game data update but no game is running! User: ' + this.getParseUser().displayName);
                console.warn(update);
                return;
            }

            playerId = (update.hasOwnProperty('slotId')) ? update.slotId : ((update.class === 'W3Player') ? update.id : -1);

            if (update.class !== 'W3GameTime') {
                // console.log(update.class + ' ' + playerId);
                // console.log(update);
            }
            if (update.class === 'W3Clock') {
                if (String(update.matchId) !== String(this.state.game.id) || !validClock(update)) return;
                const clock = { sample: update.sample, times: [...update.times], rates: [...update.rates], gameTime: update.gameTime, receivedAt: Date.now() };
                const before = this.state.game;
                this.state.game = { ...before, clock };
                this.sendStateDiff(EStateKeys.GAME_STATE, diff(before, this.state.game));
            } else if (update.class === 'W3GameTime' && this.state.settings.topBarGameDurationEnabled) {
                const gameState: IGameState = Object.assign({}, this.state.game);
                gameState.gameTime = update.value;
                const gameStateDifferences = diff(this.state.game, gameState);
                if (gameStateDifferences) {
                    this.state.game = gameState;
                    this.sendStateDiff(EStateKeys.GAME_STATE, gameStateDifferences);
                }
            } else if (update.class === 'W3ChatbarState') {
                const miscState: IMiscState = Object.assign({}, this.state.misc);
                miscState.chatbarOpen = (Number(update.value) === 1);
                const miscStateDifferences = diff(this.state.misc, miscState);
                if (miscStateDifferences) {
                    this.state.misc = miscState;
                    this.sendStateDiff(EStateKeys.MISC_STATE, miscStateDifferences);
                }
            } else if (update.class === 'W3AdvancedObserverMode') {
                if (update.value !== null && typeof update.value !== 'boolean') return;
                const miscState: IMiscState = { ...this.state.misc };
                if (update.value === null) delete miscState.advancedObserverMode;
                else miscState.advancedObserverMode = update.value;
                const differences = diff(this.state.misc, miscState);
                if (differences) {
                    this.state.misc = miscState;
                    this.sendStateDiff(EStateKeys.MISC_STATE, differences);
                }
            } else if (update.class === 'W3MenuState') {
                if (update.value !== null && typeof update.value !== 'boolean') return;
                const miscState: IMiscState = { ...this.state.misc };
                if (update.value === null) delete miscState.menuOpen;
                else miscState.menuOpen = update.value;
                const differences = diff(this.state.misc, miscState);
                if (differences) {
                    this.state.misc = miscState;
                    this.sendStateDiff(EStateKeys.MISC_STATE, differences);
                }
            } else if (update.class === 'W3HeroBar') {
                const value = update.value;
                if (value !== null && !(typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 32)) return;
                const miscState: IMiscState = { ...this.state.misc };
                if (value === null) delete miscState.heroBarLastOccupiedSlot;
                else miscState.heroBarLastOccupiedSlot = value;
                const differences = diff(this.state.misc, miscState);
                if (differences) {
                    this.state.misc = miscState;
                    this.sendStateDiff(EStateKeys.MISC_STATE, differences);
                }
            } else if (update.class === 'W3HudScale') {
                const miscState: IMiscState = Object.assign({}, this.state.misc);
                const hudScale = this.validateHudScale(update.value);
                if (hudScale === undefined) return;
                miscState.hudScale = hudScale;
                console.log('hudscale:', miscState.hudScale);
                const miscStateDifferences = diff(this.state.misc, miscState);
                if (miscStateDifferences) {
                    this.state.misc = miscState;
                    this.sendStateDiff(EStateKeys.MISC_STATE, miscStateDifferences);
                }
            } else if (update.class === 'W3TeamColor') {
                const miscState: IMiscState = Object.assign({}, this.state.misc);
                miscState.teamColors = Boolean(update.value);
                const miscStateDifferences = diff(this.state.misc, miscState);
                if (miscStateDifferences) {
                    this.state.misc = miscState;
                    this.sendStateDiff(EStateKeys.MISC_STATE, miscStateDifferences);
                }
            } else if (update.class === 'W3Player') {
                if (this.state.game.players[update.id] && (this.isObserverOrReplay() || update.id === this.state.game.realBroadcasterId)) {
                    const gameState: IGameState = JSON.parse(JSON.stringify(this.state.game));
                    const players = Object.values(gameState.players);
                    if (players.find(p => p.id === update.id) === undefined) {
                        console.warn('PLAYER NOT FOUND!!');
                        console.log(update);
                        console.log(gameState.players);
                    }
                    if (update.controlgroups === null) delete players.find(p => p.id === update.id).controlgroups;
                    else if (validControlGroups(update.controlgroups)) players.find(p => p.id === update.id).controlgroups = update.controlgroups;

                    const gameStateDifferences = diff(this.state.game, gameState);
                    if (gameStateDifferences) {
                        this.state.game = gameState;
                        this.sendStateDiff(EStateKeys.GAME_STATE, gameStateDifferences);
                    }
                }
            } else if (['W3PlayerStatistics', 'W3PlayerLosses', 'W3MatchOutcomes'].includes(update.class)) {
                if (String(update.matchId) !== String(this.state.game.id)) return;
                const gameState: IGameState = JSON.parse(JSON.stringify(this.state.game));
                if (update.class === 'W3MatchOutcomes') {
                    if (!validMatchOutcomes(update.outcomes) || !Object.keys(update.outcomes).every(id => gameState.players[id])) return;
                    gameState.outcomes = Object.fromEntries(Object.entries<MatchOutcomes[string]>(update.outcomes).filter(([id]) =>
                        this.isObserverOrReplay() || id === String(gameState.realBroadcasterId)));
                } else {
                    if (!gameState.players[playerId] || gameState.players[playerId].team === 24 ||
                        (!this.isObserverOrReplay() && playerId !== gameState.realBroadcasterId)) return;
                    const key = update.class === 'W3PlayerLosses' ? 'losses' : 'statistics';
                    const value = update.class === 'W3PlayerLosses' ? update.losses : update.statistics;
                    if (value !== null && !(key === 'losses' ? validPlayerLosses(value) : validPlayerStatistics(value))) return;
                    if (value === null) delete gameState.players[playerId][key];
                    else gameState.players[playerId][key] = JSON.parse(JSON.stringify(value));
                }
                const changes = diff(this.state.game, gameState);
                if (changes) { this.state.game = gameState; this.sendStateDiff(EStateKeys.GAME_STATE, changes); }
            } else if (update.class === 'W3PlayerMetrics') {
                if (String(update.matchId) !== String(this.state.game.id)) return;
                if (!this.state.game.players[playerId] || (!this.isObserverOrReplay() && playerId !== this.state.game.realBroadcasterId)) return;
                if (update.apm !== null && (!Number.isSafeInteger(update.apm) || update.apm < 0)) return;
                const gameState: IGameState = JSON.parse(JSON.stringify(this.state.game));
                if (update.apm === null) delete gameState.players[playerId].apm;
                else gameState.players[playerId].apm = update.apm;
                const changes = diff(this.state.game, gameState);
                if (changes) {
                    this.state.game = gameState;
                    this.sendStateDiff(EStateKeys.GAME_STATE, changes);
                }
            } else if (update.class === 'W3PointsOfInterest') {
                if (String(update.matchId) !== String(this.state.game.id)) return;
                const observer = this.isObserverOrReplay();
                if (!observer && (this.state.pois !== undefined || update.mode !== 'initial' || update.gameTime !== 0 || !validInitialPoiCollection(update.pois))) return;
                if (observer && !(update.mode === 'live' && (update.pois === null || validPoiCollection(update.pois)) ||
                    update.mode === 'initial' && validInitialPoiCollection(update.pois))) return;
                const before = this.state.pois;
                const previousMode = this.state.poiMode;
                if (update.pois === null) { delete this.state.pois; delete this.state.poiMode; }
                else { this.state.pois = JSON.parse(JSON.stringify(update.pois)); this.state.poiMode = update.mode; }
                const changes = diff(before, this.state.pois);
                if (changes || previousMode !== this.state.poiMode) this.sendStateSnapshot(EStateKeys.POIS_STATE);
            } else if (update.class === 'W3UnitHealth') {
                this.updateUnitHealth(update);
            } else if (update.class === 'W3ProductionQueue') {
                this.updateProductionQueue(update);
            } else if (update.class === 'W3Resource') {
                 const resources = Object.assign({}, this.state.resources);
                // Replays include neutral slots, including transferred armies
                // after a player leaves. They are not participating players.
                if (!this.playerStates.has(playerId) || (!this.isObserverOrReplay() && playerId !== this.state.game.realBroadcasterId)) return;
                const resourceState = this.playerStates.get(playerId).resources[playerId];
                if (![EResource.GOLD, EResource.LUMBER, EResource.SUPPLY, EResource.SUPPLY_CAP, EResource.WORKER_SUPPLY].includes(update.type) ||
                    (update.value !== null && (typeof update.value !== 'number' || !Number.isFinite(update.value) || update.value < 0))) return;
                if (update.value === null) {
                    delete resourceState[update.type];
                } else if(update.type === EResource.GOLD || update.type === EResource.LUMBER) {
                    resourceState[update.type] = update.value / 10;
                } else {
                    resourceState[update.type] = update.value;
                }
                resources[playerId] = JSON.parse(JSON.stringify(resourceState));

                if (this.isObserverOrReplay() || playerId === this.state.game.realBroadcasterId) {
                    const resourcesStateDifferences = diff(this.state.resources, resources);
                    if (resourcesStateDifferences) {
                        this.state.resources = resources;
                        this.sendStateDiff(EStateKeys.RESOURCES_STATE, resourcesStateDifferences);
                    }
                }
                
            } else if (update.class === 'W3PlayerSlot') {
                const newBroadcasterId = Number(update.value);
                if (newBroadcasterId !== this.state.game.broadcasterId || newBroadcasterId !== this.state.game.realBroadcasterId) {
                    this.state.game.broadcasterId = newBroadcasterId;
                    this.state.game.realBroadcasterId = newBroadcasterId;
                    this.onAfterBroadcasterChange();
                    this.sendAllStateSnapshots();
                    console.log('Switched visible player to :' + this.getBroadcastingPlayer().name);
                }
            } else if (update.class === 'W3Unit' && update.isHero && playerId >= 0) {
                if ((!update.removed && typeof update.isIllusion !== 'boolean') ||
                    String(update.matchId) !== String(this.state.game.id) ||
                    typeof update.id !== 'string' || !/^[0-9a-f]{16}$/.test(update.id) ||
                    typeof update.typeId !== 'string' || !/^[A-Za-z0-9]{4}$/.test(update.typeId) ||
                    !this.playerStates.has(playerId)) return;
                // Remove this instance from a previous owner, never another hero of its type.
                for (const [owner, wrapper] of this.playerStates) {
                    if (!update.removed && owner !== playerId && wrapper.heroes?.[owner]?.[update.id]) {
                        delete wrapper.heroes[owner][update.id];
                        if (this.state.heroes?.[owner]?.[update.id]) {
                            const before = this.state.heroes;
                            this.state.heroes = { ...before, [owner]: { ...wrapper.heroes[owner] } };
                            this.sendStateDiff(EStateKeys.HEROES_STATE, diff(before, this.state.heroes));
                        }
                    }
                }

                const heroes = Object.assign({}, this.state.heroes);
                const heroesState = this.playerStates.get(playerId).heroes[playerId];
                if (update.removed === true) {
                    if (!heroesState[update.id]) return;
                    delete heroesState[update.id];
                    heroes[playerId] = { ...heroesState };
                    const changes = diff(this.state.heroes, heroes);
                    if (changes) { this.state.heroes = heroes; this.sendStateDiff(EStateKeys.HEROES_STATE, changes); }
                    return;
                }
                const pools = this.state.units?.[update.id];
                const heroOrder = Number.isInteger(update.heroOrder) && update.heroOrder > 0 && update.heroOrder <= 0x7fffffff
                    ? update.heroOrder : heroesState[update.id]?.heroOrder;
                const heroState: IHeroState = {
                    ...(heroOrder !== undefined ? { heroOrder } : {}),
                    id: update.id,
                    typeId: update.typeId,
                    isIllusion: update.isIllusion,
                    experience: (update.experience) ? Number(update.experience) : 0,
                    hitpoints: update.hitpoints ?? pools?.hitpoints,
                    mana: update.mana ?? pools?.mana,
                    combat: pools?.combat,
                    abilities: new Array(),
                    items: new Array()
                };
                this.updateUnitIllusionFromMetadata(update);

                if (update.abilities) {
                    for (const ability of [...update.abilities].sort((a, b) => Number(b.order ?? 0) - Number(a.order ?? 0))) {
                        const typeId = ability.type;
                        heroState.abilities.push({ typeId, level: ability.level, lastActivation: ability.lastActivation, id: 'A' + update.id + typeId });
                    }
                }

                if (update.inventory) {
                    heroState.items = update.inventory;
                }
                if (update.inventoryCooldowns !== undefined) {
                    if (!Array.isArray(update.inventoryCooldowns) || update.inventoryCooldowns.length !== heroState.items.length ||
                        update.inventoryCooldowns.length > 6 || update.inventoryCooldowns.some((value, slot) => value !== null &&
                            (!heroState.items[slot] || !validTimedProgress(value)))) return;
                    heroState.inventoryCooldowns = update.inventoryCooldowns.map(value => value === null ? null : timedProgress(value));
                }
                if (update.inventoryCharges !== undefined) {
                    if (!Array.isArray(update.inventoryCharges) || update.inventoryCharges.length !== heroState.items.length ||
                        update.inventoryCharges.length > 6 || update.inventoryCharges.some((value, slot) => value !== null &&
                            (!heroState.items[slot] || !Number.isInteger(value) || value < 0 || value > 0x7fffffff))) return;
                    heroState.inventoryCharges = [...update.inventoryCharges];
                }

                heroesState[update.id] = heroState;
                console.log('Hero State Update for user :' + this.state.game.players[playerId].name);

                heroes[playerId] = JSON.parse(JSON.stringify(heroesState));

                if (this.isObserverOrReplay() || playerId === this.state.game.broadcasterId) {
                    const heroStateDifferences = diff(this.state.heroes, heroes);
                    if (heroStateDifferences) {
                        console.log('Submitted differences:');
                        console.log(heroStateDifferences);
                        this.state.heroes = heroes;
                        this.sendStateDiff(EStateKeys.HEROES_STATE, heroStateDifferences);
                    }
                }
            } else if (update.class === 'W3Research' && playerId >= 0) {
                // Empty native research slots are not completed upgrades. Never
                // let their null rawcodes/zero levels enter the public state.
                if (typeof update.type !== 'string' || update.type.length !== 4 || update.type.includes('\0') ||
                    !Number.isInteger(update.level) || update.level < 1) return;
                const upgrades = Object.assign({}, this.state.upgrades);
                // Replays include neutral slots, including transferred armies
                // after a player leaves. They are not participating players.
                if (!this.playerStates.has(playerId)) return;
                const upgradeState: IUpgradeState = this.playerStates.get(playerId).upgrades[playerId];
                // upgradeState.upgrades = JSON.parse(JSON.stringify(this.state.upgrades.upgrades));
                // upgradeState.active = JSON.parse(JSON.stringify(this.state.upgrades.active));
                // upgradeState.researching = [];

                upgradeState.upgrades.push({ typeId: update.type, level: update.level, gametime: Date.now() });
                if (upgradeState.active.find((u) => u.typeId === update.type)) {
                    upgradeState.active.find((u) => u.typeId === update.type).level = update.level;
                } else {
                    upgradeState.active.push({ typeId: update.type, gametime: Date.now(), level: update.level });
                }

                console.log('Upgrade State Update for user :' + this.state.game.players[playerId].name);
                console.log(upgradeState);
                upgrades[playerId] = JSON.parse(JSON.stringify(upgradeState));

                if (this.isObserverOrReplay() || playerId === this.state.game.broadcasterId) {
                    const upgradeStateDifferences = diff(this.state.upgrades, upgrades);
                    if (upgradeStateDifferences) {
                        this.state.upgrades = upgrades;
                        this.sendStateDiff(EStateKeys.UPGRADES_STATE, upgradeStateDifferences);
                    }
                }
            }
        } catch (e) {
            console.warn('ISSUE with Update:');
            console.warn(update);
            console.warn('PLAYER-ID was:' + playerId);
            console.warn(e);
            console.warn(this.state);
        }
    }

    public async onSettingsChanged() {
        if (this.isGameRunning()) {
            this.state.settings = await this.getSettings(this.isObserverOrReplay());
            console.log(this.state.settings);
            this.sendStateSnapshot(EStateKeys.SETTINGS_STATE);
        }
    }

    public getBroadcastingPlayer(): IPlayer {
        return this.state.game.players[this.state.game.broadcasterId];
    }

    private validateHudScale(scale: unknown): number | undefined {
        return typeof scale === 'number' && Number.isFinite(scale) && scale >= 0.5 && scale <= 1 ? scale : undefined;
    }

    private updateUnitIllusionFromMetadata(update: any): void {
        const observed = this.state.units?.[update.id];
        if (!observed || observed.slotId !== update.slotId || observed.isIllusion === update.isIllusion) return;
        const before = this.state.units;
        this.state.units = { ...before, [update.id]: { ...observed, isIllusion: update.isIllusion } };
        this.sendStateDiff(EStateKeys.UNITS_STATE, diff(before, this.state.units));
    }

    private updateProductionQueue(update: any): void {
        if (String(update.matchId) !== String(this.state.game.id) ||
            typeof update.id !== 'string' || !/^[0-9a-f]{16}$/.test(update.id)) return;
        const previous = this.state.production || {};
        const production = { ...previous };
        if (update.removed === true) {
            if (!production[update.id] || production[update.id].slotId !== update.slotId) return;
            delete production[update.id];
        } else {
            const slot = update.slotId;
            if (typeof update.isIllusion !== 'boolean') return;
            if (!Number.isInteger(slot) || slot < 0 || slot >= 24 || !this.state.game.players[slot] ||
                (!this.isObserverOrReplay() && slot !== this.state.game.broadcasterId) ||
                typeof update.typeId !== 'string' || !/^[A-Za-z0-9]{4}$/.test(update.typeId) ||
                !Array.isArray(update.queue) || update.queue.length > 16 ||
                update.queue.some((entry: any, index: number) => !entry || entry.position !== index ||
                    typeof entry.typeId !== 'string' || !/^[A-Za-z0-9]{4}$/.test(entry.typeId) ||
                    !validTimedProgress(entry) || (entry.buildType !== undefined && !['research', 'unit', 'reviving'].includes(entry.buildType)) ||
                    (index !== 0 && (entry.timing !== undefined || entry.remainingSeconds !== null || entry.totalSeconds !== null)))) return;
            production[update.id] = { id: update.id, slotId: slot, typeId: update.typeId, isIllusion: update.isIllusion,
                queue: update.queue.map((entry: any) => ({ position: entry.position, typeId: entry.typeId, ...(entry.buildType === undefined ? {} : { buildType: entry.buildType }), ...timedProgress(entry) })) };
            this.updateUnitIllusionFromMetadata(update);
        }
        const differences = diff(previous, production);
        if (!differences) return;
        this.state.production = production;
        this.sendStateDiff(EStateKeys.PRODUCTION_STATE, differences);
    }

    private updateUnitHealth(update: any): void {
        if (String(update.matchId) !== String(this.state.game.id) ||
            typeof update.id !== 'string' || !/^[0-9a-f]{16}$/.test(update.id)) return;
        const previous = this.state.units || {};
        const units = { ...previous };
        if (update.removed === true) {
            // A stale removal for the previous owner must not remove a transferred unit.
            if (!units[update.id] || units[update.id].slotId !== update.slotId) return;
            delete units[update.id];
        } else {
            const slot = update.slotId;
            const hp = update.hitpoints;
            if (typeof update.isIllusion !== 'boolean') return;
            if (!Number.isInteger(slot) || slot < 0 || slot >= 24 || !this.state.game.players[slot] ||
                (!this.isObserverOrReplay() && slot !== this.state.game.broadcasterId) ||
                typeof update.typeId !== 'string' || !/^[A-Za-z0-9]{4}$/.test(update.typeId) ||
                !hp || !validTiming(hp) || !Number.isFinite(hp.current) || !Number.isFinite(hp.max) || hp.max <= 0 ||
                hp.current < 0 || hp.current > hp.max) return;
            if (!Number.isInteger(update.targetFlags) || update.targetFlags <= 0 || update.targetFlags >= 2048) return;
            const construction = update.construction;
            if (construction !== undefined && (!construction || /^[A-Z]/.test(update.typeId) || !(update.targetFlags & 8) ||
                !validTimedProgress(construction))) return;
            const upgrade = update.upgrade;
            if (upgrade !== undefined && (!upgrade || /^[A-Z]/.test(update.typeId) || !(update.targetFlags & 8) ||
                !validTimedProgress(upgrade) || typeof upgrade.typeId !== 'string' || !/^[A-Za-z0-9]{4}$/.test(upgrade.typeId))) return;
            const position = update.position;
            if (position !== undefined && ((!/^[A-Z]/.test(update.typeId) && !(update.targetFlags & 8)) || !position || !Number.isFinite(position.x) || !Number.isFinite(position.y) ||
                Math.abs(position.x) > 1000000 || Math.abs(position.y) > 1000000)) return;
            const combat = update.combat;
            if (combat !== undefined && (!/^[A-Z]/.test(update.typeId) || !combat ||
                !['damageDealt', 'selfDamage', 'damageReceived', 'healingDealt'].every(key => Number.isFinite(combat[key]) && combat[key] >= 0) ||
                combat.selfDamage > combat.damageDealt || combat.selfDamage > combat.damageReceived ||
                combat.damage !== undefined && !validDamageSummary(combat.damage, combat.damageDealt))) return;
            const isHero = /^[A-Z]/.test(update.typeId);
            if (isHero ? update.unitMana !== undefined : update.mana !== undefined) return;
            const mana = isHero ? update.mana : update.unitMana;
            if (mana !== undefined && (!mana || !validTiming(mana) ||
                !Number.isFinite(mana.current) || !Number.isFinite(mana.max) ||
                mana.max < 0 || mana.current < 0 || mana.current > mana.max ||
                mana.regenerationPerSecond !== undefined && !Number.isFinite(mana.regenerationPerSecond))) return;
            units[update.id] = { id: update.id, slotId: slot, typeId: update.typeId, isIllusion: update.isIllusion,
                targetFlags: update.targetFlags,
                ...(combat === undefined ? {} : { combat: { damageDealt: combat.damageDealt, selfDamage: combat.selfDamage,
                    damageReceived: combat.damageReceived, healingDealt: combat.healingDealt,
                    ...(combat.damage === undefined ? {} : { damage: damageSummary(combat.damage) }) } }),
                ...(construction === undefined ? {} : { construction: timedProgress(construction) }),
                ...(upgrade === undefined ? {} : { upgrade: { ...timedProgress(upgrade), typeId: upgrade.typeId } }),
                ...(position === undefined ? {} : { position: { x: position.x, y: position.y } }),
                hitpoints: { current: hp.current, max: hp.max, ...timingFields(hp) },
                ...(mana === undefined ? {} : { mana: { current: mana.current, max: mana.max, ...timingFields(mana),
                    ...(mana.regenerationPerSecond === undefined ? {} : { regenerationPerSecond: mana.regenerationPerSecond }) } }) };
        }
        // Only this instance changed. Diffing the whole army here repeats a
        // traversal of every unchanged unit for each native health observation.
        const differences = diff(
            previous[update.id] === undefined ? {} : { [update.id]: previous[update.id] },
            units[update.id] === undefined ? {} : { [update.id]: units[update.id] });
        if (!differences) return;
        this.state.units = units;
        this.sendStateDiff(EStateKeys.UNITS_STATE, differences);

        // Health and queue packets may arrive in either order. Retain the latest
        // observed classification even when the queue contents did not change.
        const queued = this.state.production?.[update.id];
        if (!update.removed && queued?.slotId === update.slotId && queued.isIllusion !== update.isIllusion) {
            const before = this.state.production;
            this.state.production = { ...before, [update.id]: { ...queued, isIllusion: update.isIllusion } };
            this.sendStateDiff(EStateKeys.PRODUCTION_STATE, diff(before, this.state.production));
        }

        // Hero pools use the exact instance identity, never owner/type matching.
        const id = update.id;
        const existing = this.state.heroes?.[update.slotId]?.[id];
        if (existing) {
            const hero = { ...existing };
            if (update.removed === true) {
                delete hero.hitpoints;
                delete hero.mana;
                delete hero.combat;
            } else {
                hero.isIllusion = units[update.id].isIllusion;
                hero.hitpoints = units[update.id].hitpoints;
                if (units[update.id].combat) hero.combat = units[update.id].combat;
                else delete hero.combat;
                if (units[update.id].mana) hero.mana = units[update.id].mana;
                else delete hero.mana;
            }
            const heroes = { ...this.state.heroes, [update.slotId]: { ...this.state.heroes[update.slotId], [id]: hero } };
            const heroDifferences = diff(
                { [update.slotId]: { [id]: existing } },
                { [update.slotId]: { [id]: hero } });
            if (heroDifferences) {
                this.state.heroes = heroes;
                const player = this.playerStates.get(update.slotId);
                if (player?.heroes?.[update.slotId]) player.heroes[update.slotId][id] = hero;
                this.sendStateDiff(EStateKeys.HEROES_STATE, heroDifferences);
            }
        }
    }

    private onAfterBroadcasterChange() {

        if (this.playerStates.has(this.state.game.broadcasterId) && !this.isObserverOrReplay()) {
            this.state.heroes = JSON.parse(JSON.stringify(this.playerStates.get(this.state.game.broadcasterId).heroes));
            this.state.upgrades = JSON.parse(JSON.stringify(this.playerStates.get(this.state.game.broadcasterId).upgrades));
        } else if (!this.playerStates.has(this.state.game.broadcasterId)) {
            this.state.heroes = {};
            this.state.upgrades = {};
            for (const playerStateWrapper of Array.from(this.playerStates.values())) {
                Object.assign(this.state.heroes, JSON.parse(JSON.stringify(playerStateWrapper.heroes)));
                Object.assign(this.state.upgrades, JSON.parse(JSON.stringify(playerStateWrapper.upgrades)));
            }
            this.state.game.broadcasterId = Number(Object.keys(this.state.game.players)[0]);
            console.log('SWITCHED TO OBSERVER, viewing as the following player:' + this.getBroadcastingPlayer().name);
        }
        this.state.game.isObserver = this.state.game.isObserver || (this.state.game.broadcasterId !== this.state.game.realBroadcasterId);
        if (!this.isObserverOrReplay()) {
            if (this.state.units) {
                const units = { ...this.state.units };
                for (const id of Object.keys(units)) {
                    if (units[id].slotId !== this.state.game.broadcasterId) delete units[id];
                }
                this.state.units = units;
            }
            if (this.state.production) {
                const production = { ...this.state.production };
                for (const id of Object.keys(production)) {
                    if (production[id].slotId !== this.state.game.broadcasterId) delete production[id];
                }
                this.state.production = production;
            }
        }
    }


    private initState() {
        this.state = {
            misc: {
                chatbarOpen: undefined,
                hudScale: 1,
                teamColors: undefined,
                localServerUrls: [],
            },
            settings: {} as any,
            game: {
                id: Math.random(),
                state: EGameStateEnum.NO_GAME,
                paused: false,
                pausedTime: 0,
                gameTime: 0,
                gameMode: EGameModeEnum.GM_UNDEFINED,
                players: {}
            },
            heroes: {},
            upgrades: {},
            resources: {},
        };
        this.playerStates = new Map();
    }

    private initPlayerState(playerId: number) {
        const playerStateWrapper: IPlayerStateWrapper = {
            heroes: {},
            upgrades: {},
            resources: {},
        };

        playerStateWrapper.heroes[playerId] = {};
        playerStateWrapper.upgrades[playerId] = {
            upgrades: [],
            active: [],
            researching: []
        };
        playerStateWrapper.resources[playerId] = {};

        this.playerStates.set(playerId, playerStateWrapper);
    }
}
